import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Users, Briefcase, CheckCircle } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const Mentors = () => {
  const { token, user } = useContext(AuthContext);
  const { t } = useLanguage();
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMentors();
  }, []);

  const fetchMentors = async () => {
    try {
      const response = await axios.get(`${API}/mentors`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMentors(response.data);
    } catch (error) {
      toast.error("Failed to load mentors");
    } finally {
      setLoading(false);
    }
  };

  const handleRequest = async (mentorId) => {
    try {
      await axios.post(`${API}/mentors/${mentorId}/request`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Mentorship request sent successfully!");
      fetchMentors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send request");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8" data-testid="mentors-container">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
            <Users className="w-10 h-10 text-indigo-500" />
            {t('mentors.title')}
          </h1>
          <p className="text-gray-600 text-lg">{t('mentors.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {mentors.map((mentor) => {
            const isConnected = mentor.mentees?.includes(user?.id);
            
            return (
              <Card key={mentor.id} className="card-hover" data-testid={`mentor-card-${mentor.id}`}>
                <CardHeader className="text-center">
                  {mentor.image ? (
                    <img 
                      src={mentor.image} 
                      alt={mentor.name}
                      className="w-24 h-24 rounded-full mx-auto mb-4 object-cover border-4 border-indigo-200"
                    />
                  ) : (
                    <Avatar className="w-24 h-24 mx-auto mb-4 bg-gradient-to-br from-indigo-500 to-purple-600">
                      <AvatarFallback className="text-white text-3xl font-bold">
                        {mentor.name.split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                  )}
                  <CardTitle className="text-xl">{mentor.name}</CardTitle>
                  <CardDescription>
                    <div className="flex items-center justify-center gap-2 mt-2">
                      <Briefcase className="w-4 h-4" />
                      <span>{mentor.profession}</span>
                    </div>
                    <p className="mt-1 font-semibold text-indigo-600">{mentor.company}</p>
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm font-semibold mb-2">{t('mentors.expertise')}:</p>
                    <div className="flex flex-wrap gap-2">
                      {mentor.expertise.map((skill, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">{skill}</Badge>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">{mentor.bio}</p>
                  <div className="flex items-center gap-2 text-sm">
                    {mentor.availability ? (
                      <Badge className="bg-green-100 text-green-700">{t('mentors.available')}</Badge>
                    ) : (
                      <Badge variant="secondary">{t('mentors.currentlyFull')}</Badge>
                    )}
                    {isConnected && (
                      <Badge className="bg-blue-100 text-blue-700">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        {t('mentors.connected')}
                      </Badge>
                    )}
                  </div>
                  <Button 
                    className="w-full" 
                    onClick={() => handleRequest(mentor.id)}
                    disabled={isConnected || !mentor.availability}
                    data-testid={`request-mentor-${mentor.id}`}
                  >
                    {isConnected ? t('mentors.alreadyConnected') : !mentor.availability ? t('mentors.currentlyFull') : t('mentors.connectWithMentor')}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {mentors.length === 0 && (
          <div className="text-center py-12" data-testid="no-mentors-message">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">{t('mentors.noResults')}</p>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Mentors;